'use client';

import { useEffect } from 'react';

/**
 * Código embebido del tenant: el snippet HTML/JS que la automotora pega en el
 * builder (`client_website_config.integrations.embed_code`) y que acá se inyecta
 * al final del <body>. Chats, widgets de reseñas, encuestas: lo que el proveedor
 * entregue.
 *
 * No se puede resolver con `dangerouslySetInnerHTML`: los <script> insertados vía
 * innerHTML NO se ejecutan (spec de HTML). Hay que re-crear cada <script> con
 * `document.createElement` para que el navegador lo evalúe. Dos cuidados:
 *
 * - `async = false` en los externos: un script creado por JS es async por
 *   defecto, así que dos <script src> se ejecutarían en orden de descarga. Con
 *   async=false vuelven a ser "in-order" y respetan el orden del snippet. Importa
 *   cuando el proveedor pide un config inline ANTES de su loader (Vambe hace eso:
 *   webchat.js lee `window.embeddedWebchatConfig` una sola vez al ejecutar y si
 *   no existe aborta sin reintentar).
 *
 * - Guard por `data-goauto-embed`: en dev React monta dos veces (StrictMode) y en
 *   navegaciones de cliente el layout puede remontarse; sin el guard quedarían
 *   dos widgets encima.
 *
 * Es contenido del propio tenant, equivalente a lo que ya podían meter por GTM.
 */

const CONTAINER_ATTR = 'data-goauto-embed';

export default function EmbeddedCode({ code }: { code?: string }) {
  useEffect(() => {
    const snippet = code?.trim();
    if (!snippet) return;
    // Ya hay un contenedor vivo (StrictMode / remonte): no duplicar.
    if (document.querySelector(`[${CONTAINER_ATTR}]`)) return;

    const container = document.createElement('div');
    container.setAttribute(CONTAINER_ATTR, '');
    document.body.appendChild(container);

    // <template> parsea el HTML sin ejecutar nada ni bajar recursos.
    const template = document.createElement('template');
    template.innerHTML = snippet;

    Array.from(template.content.childNodes).forEach((node) => {
      if (node.nodeName === 'SCRIPT') {
        const original = node as HTMLScriptElement;
        const script = document.createElement('script');
        Array.from(original.attributes).forEach((attr) =>
          script.setAttribute(attr.name, attr.value)
        );
        if (original.src && !original.hasAttribute('async')) script.async = false;
        script.text = original.text;
        container.appendChild(script);
      } else {
        container.appendChild(node);
      }
    });

    return () => {
      container.remove();
    };
  }, [code]);

  return null;
}
